import { useState, useEffect, useCallback } from 'react';
import { getExploreCategoriesV1 } from '@/services/platform/api/explore/explore';
import type { GetExploreCategoriesV1Params } from '@/services/platform/api/model';
import type { ServiceCategoryDto } from '@/services/platform/api/model';

export interface ExploreCategoriesState {
  categories: ServiceCategoryDto[];
  isLoading: boolean;
  refetch: () => void;
}

export function useExploreCategories(
  params?: GetExploreCategoriesV1Params,
  options?: { enabled?: boolean }
): ExploreCategoriesState {
  const [categories, setCategories] = useState<ServiceCategoryDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const enabled = options?.enabled ?? true;

  const fetch = useCallback(
    async (isStale: () => boolean) => {
      if (!enabled) return;
      setIsLoading(true);
      try {
        const response = await getExploreCategoriesV1(params);
        if (isStale()) return;
        if (response.status === 200) {
          setCategories(response.data);
        }
      } catch {
        // silent — caller checks categories === []
      } finally {
        if (!isStale()) setIsLoading(false);
      }
    },
    [enabled, params?.locationNodeKey]
  );

  useEffect(() => {
    let stale = false;
    fetch(() => stale);
    return () => {
      stale = true;
    };
  }, [fetch]);

  return { categories, isLoading, refetch: () => fetch(() => false) };
}
